import React, { ChangeEvent, Component } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { File, Check, FileText } from "lucide-react";
import { KycDocument } from "../../models/DocumentFile";
import { utils } from "../../utils/utils";
import { toastService } from "../../services/toastService";
import { nanoid } from "nanoid";

interface Props {
    isSubmitting: boolean;
    onSubmit: (documents: KycDocument[]) => void;
    onHide: () => void;
}

interface State {
    documents: KycDocument[];
}

class UploadSourceOfFundsDialog extends Component<Props, State> {
    maxFileSize = 5242880;
    imageInput: HTMLInputElement | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            documents: [],
        };
    }

    handleSubmit = () => {
        this.props.onSubmit(this.state.documents);
    };

    renderFooter = () => {
        return (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <div>
                    <Button
                        label="Choose"
                        severity="success"
                        icon={<File size={16} />}
                        iconPos="right"
                        onClick={() => this.imageInput?.click()}
                        disabled={this.props.isSubmitting}
                    />
                    <input
                        ref={(el) => (this.imageInput = el)}
                        type="file"
                        style={{ display: "none" }}
                        accept="image/jpeg, image/png, application/pdf"
                        onChange={this.onImageSelected}
                        multiple
                    />
                </div>
                <Button
                    type="button"
                    icon={<Check size={16} />}
                    severity="success"
                    iconPos="right"
                    label="Proceed"
                    onClick={this.handleSubmit}
                    loading={this.props.isSubmitting}
                    disabled={this.state.documents.length == 0 ||this.props.isSubmitting}
                />
            </div>
        );
    };

    onImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        const newDocuments: KycDocument[] = [];

        for (const file of Array.from(files)) {
            if (!utils.checkCharacterlength(file.name)) {
                toastService.error(`File name "${file.name}" exceeds 128 characters.`, "File Name");
                continue;
            }

            if (!allowedTypes.includes(file.type)) {
                toastService.error(`File "${file.name}" has unsupported format.`, "File Type");
                continue;
            }

            const fileData = await utils.convertFileToBase64(file);

            newDocuments.push({
                file,
                fileName: file.name,
                fileData: fileData as string,
                objectURL: URL.createObjectURL(file),
                type: "",
            });
        }

        this.setState((prevState) => ({
            documents: [...prevState.documents, ...newDocuments],
        }));

        // Clear input so selecting the same file again triggers change
        if (this.imageInput) this.imageInput.value = "";
    };

    render() {
        const { isSubmitting, onHide } = this.props;
        const { documents } = this.state;

        return (
            <Dialog
                header="Upload Source of Funds"
                visible={true}
                style={{ width: "40vw" }}
                modal
                onHide={onHide}
                footer={this.renderFooter()}
            >
                <div style={{ display: "flex", alignItems: "center" }} key={nanoid()}>
                    {documents.length > 0 && (
                        <div className="mt-3 w-100">
                            {documents.map((doc, index) => (
                                <div className="d-flex align-items-center mb-2" key={index}>
                                    {doc.file?.type.startsWith("image/") ? (
                                        <img src={doc.objectURL ?? ""} alt="Preview" width={50} height={50} style={{ objectFit: "cover", marginRight: 10 }} />
                                    ) : (
                                        <FileText size={24} style={{ marginRight: 10 }} />
                                    )}
                                    <span>{doc.fileName}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Dialog>
        );
    }
}

export default UploadSourceOfFundsDialog;
